<script setup>
import { onMounted, reactive, ref, defineProps, defineEmits } from "vue";
import { apiQuery } from "@/api/j24api";
import convertToCsv from "./ConvertToCSV.js";
import ElevateESFEvent from "./ElevateESFEvent.vue";

const activeTab = ref("main");
const loading = ref(true);
const props = defineProps({
  licenceName: String,
  from: Date,
  to: Date,
  serialNumber: String,
  username: String,
  activateESF: Boolean,
  customQuery: String,
  useV2Api: Boolean,
});
const emit = defineEmits(["back"]);
const events = reactive([]);

const selectedEsfSession = ref("");
const selectedEsfSessionUsername = ref("");
const selectedEsfSessionSerialNumber = ref("");

function selectEsf(item) {
  console.log(item);
  if (item.sessionid) {
    selectedEsfSessionUsername.value = item.user;
    selectedEsfSessionSerialNumber.value = item.serialnumber;
    selectedEsfSession.value = item.sessionid;
    activeTab.value = "esf";
  }
}

function closeEsf() {
  selectedEsfSession.value = "";
  selectedEsfSessionUsername.value = "";
  selectedEsfSessionSerialNumber.value = "";
  activeTab.value = "main";
}

const alertTxt = ref("");
const headers = [
  { text: "Username", value: "user", sortable: true },
  { text: "Serial Number", value: "serialnumber", sortable: true },
  {
    text: "Date Time Requested",
    value: "datetimerequested",
    sortable: true,
  },
  {
    text: "Length of Elevation",
    value: "lengthofelevation",
    sortable: true,
  },
  { text: "Reason", value: "reason", sortable: true },
  { text: "Reason User Text", value: "reasonusertext", sortable: true },
];

function formatSecondsToMinutesHours(seconds) {
  if (isNaN(seconds) || seconds === null) return seconds;
  const s = parseInt(seconds, 10);
  if (s < 60) return `${s} seconds`;
  const minutes = Math.floor(s / 60);
  if (minutes < 60) return `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (remMinutes === 0) return `${hours} hours`;
  return `${hours} hours ${remMinutes} minutes`;
}

async function getData() {
  loading.value = true;
  var query = {
    apiAction: "CreateRequest",
    licenceName: props.licenceName,
    from: props.from.toISOString().split(".")[0],
    to: props.to.toISOString().split(".")[0],
  };
  if (props.serialNumber) {
    query.query =
      'where length(lengthofelevation) > 0 | where serialnumber matches "' +
      props.serialNumber +
      '"';
  }
  if (props.username) {
    query.query =
      'where length(lengthofelevation) > 0 | where user matches "' +
      props.username +
      '"';
  }
  if (query.licenceName == "TestData") {
    query.licenceName = "Jigsaw24 Elevate Demo";
  }
  if (props.customQuery) {
    query.query = props.customQuery;
  }

  var result;
  if (props.useV2Api) {
    query.apiAction = "GetElevateReportDataV2";
    result = await apiQuery("ELA", "elevatereports", query);
    if (result.data?.length) {
      events.length = 0;
      let eventsToAdd = result.data.map((item) => {
        // Fix for UK Daylight Saving Time if utc is not defined or false
        if (item.dateTimeRequested && !item.utc) {
          let date = new Date(item.dateTimeRequested);
          console.log("Checking date:", item, date);
          if (isUKDaylightSaving(date)) {
            // Adjust for UK Daylight Saving Time
            date.setUTCHours(date.getUTCHours() - 1);
            item.dateTimeRequested = date.toISOString();
            console.log("Adjusting for UK Daylight Saving Time:", item);
          }
        }

        return {
          //map: {
          user: item.user,
          serialnumber: item.serialNumber,
          datetimerequested: item.dateTimeRequested,
          lengthofelevation: item.lengthOfElevation,
          reason: item.reason,
          reasonusertext: item.reasonUserText,
          sessionid: item.sessionId,
          // },
        };
      });
      // Fix for UK Daylight Saving Time if utc is not defined or false

      events.push(...eventsToAdd);
    }
  } else {
    result = await apiQuery("ELA", "elevatereports", query);
    if (!result.reportId) {
      alertTxt.value = "Failed to get data.";
      return;
    }
    let reportId = result.reportId;
    var dataRecieved = false;
    let maxRequests = 100;
    var count = 0;
    while (!dataRecieved && count < maxRequests) {
      let result = await apiQuery("ELA", "elevatereports", {
        apiAction: "GetStatus",
        reportID: reportId,
      });
      if (result?.state == "DONE GATHERING RESULTS") {
        const messages = await apiQuery("ELA", "elevatereports", {
          apiAction: "GetMessages",
          reportID: reportId,
        });
        if (messages.messages.length) {
          events.length = 0;
          let newMessages = messages.messages.map((item) => {
            return item.map;
          });
          events.push(...newMessages);
        }
        console.log("messages", messages);
        dataRecieved = true;
      }
      count++;
      await sleep(1000);
    }
  }

  loading.value = false;
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

onMounted(() => {
  getData();
  if (props.activateESF) {
    headers.push({ text: "Actions", value: "actions", sortable: false });
  }
});

function isUKDaylightSaving(date) {
  // UK DST starts last Sunday in March, ends last Sunday in October
  const year = date.getFullYear();

  // Find last Sunday in March
  let march = new Date(year, 2, 31); // March 31
  let lastSundayMarch = march.getDate() - march.getDay();
  let dstStart = new Date(year, 2, lastSundayMarch, 1, 0, 0); // 1am local time

  // Find last Sunday in October
  let october = new Date(year, 9, 31); // October 31
  let lastSundayOctober = october.getDate() - october.getDay();
  let dstEnd = new Date(year, 9, lastSundayOctober, 2, 0, 0); // 2am local time

  return date >= dstStart && date < dstEnd;
}
</script>

<template>
  <v-window v-model="activeTab">
    <v-window-item value="main">
      <v-row>
        <v-col cols="auto">
          <v-btn @click="$emit('back')" prepend-icon="mdi-arrow-left"
            >Back</v-btn
          >
        </v-col>
        <v-spacer></v-spacer>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            density="compact"
            variant="underlined"
            hide-details
            label="Search..."
            v-model="searchValue"
          />
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="auto">
          <download-csv :data="convertToCsv(events)"
            ><v-btn prepend-icon="mdi-download">Download</v-btn></download-csv
          >
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <EasyDataTable
            v-if="loading"
            class=""
            :loading="loading"
            :items="[]"
            :headers="headers"
            theme-color="#e7541e"
          />
          <EasyDataTable
            v-else
            class=""
            :loading="loading"
            :items="events"
            :headers="headers"
            theme-color="#e7541e"
            sort-by="datetimerequested"
            sort-type="desc"
            must-sort
            alternating
          >
            <template #item-datetimerequested="item">
              {{ new Date(item.datetimerequested).toLocaleString() }}
            </template>

            <template #item-lengthofelevation="item">
              {{ formatSecondsToMinutesHours(item.lengthofelevation) }}
            </template>

            <template #item-actions="item">
              <v-btn
                v-if="item.sessionid"
                icon
                size="small"
                density="compact"
                @click="selectEsf(item)"
              >
                <v-icon class="mx-1" color="primary"
                  >mdi-text-box-multiple-outline</v-icon
                >
                <v-tooltip
                  activator="parent"
                  location="start"
                  text="View Session Logs"
                />
              </v-btn>
            </template>
          </EasyDataTable>
        </v-col>
      </v-row>
    </v-window-item>

    <v-window-item value="esf">
      <ElevateESFEvent
        :licenceName="props.licenceName"
        :serialNumber="selectedEsfSessionSerialNumber"
        :username="selectedEsfSessionUsername"
        :eventReference="selectedEsfSession"
        @back="closeEsf()"
      />
    </v-window-item>
  </v-window>
</template>
